import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Modal, Box, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody,
  Select, MenuItem, FormControl, Alert
} from '@mui/material';

export interface Field {
  name: string;
  label: string;
  required?: boolean;
}

export interface MassUploadModalProps {
  open: boolean;
  onClose: () => void;
  fields: Field[];
  requiredOneOf?: string[][];
  onUpload: (data: Record<string, string>[]) => void;
}

export function MassUploadModal({ open, onClose, fields, requiredOneOf, onUpload }: MassUploadModalProps) {
  const [fileData, setFileData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, { type: 'binary' });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

      if (data.length > 0) {
        const cols = (data[0] as any[]).map((c: any) => String(c).trim());
        setColumns(cols);
        setFileData(data.slice(1).filter((row: any) => row.length > 0));

        // Auto-match
        const initialMapping: Record<string, string> = {};
        cols.forEach(col => {
          const match = fields.find(f => f.name.toLowerCase() === col.toLowerCase() || f.label.toLowerCase() === col.toLowerCase());
          if (match) {
            initialMapping[col] = match.name;
          }
        });
        setMapping(initialMapping);
      }
    };
    reader.readAsBinaryString(file);
  };

  const handleMapChange = (col: string, val: string) => {
    setMapping(prev => ({
      ...prev,
      [col]: val
    }));
  };

  const getAvailableFields = (currentCol: string) => {
    const mappedFields = Object.entries(mapping)
      .filter(([c, _]) => c !== currentCol)
      .map(([_, f]) => f);
    return fields.filter(f => !mappedFields.includes(f.name));
  };

  const handleConfirm = () => {
    const result = fileData.map((row) => {
      const obj: Record<string, string> = {};
      columns.forEach((col, idx) => {
        const fieldName = mapping[col];
        if (fieldName) {
          obj[fieldName] = String(row[idx] ?? "");
        }
      });
      return obj;
    });
    onUpload(result);
    onClose();
  };

  const handleCancel = () => {
    setFileData([]);
    setColumns([]);
    setMapping({});
    onClose();
  }

  const requiredFields = fields.filter(f => f.required).map(f => f.name);
  const mappedFields = Object.values(mapping);
  let missingOneOf = false;
  let unmappedOneOfGroups: string[][] = [];
  if (requiredOneOf) {
    unmappedOneOfGroups = requiredOneOf.filter(group => !group.some(f => mappedFields.includes(f)));
    missingOneOf = unmappedOneOfGroups.length > 0;
  }

  const unmappedRequiredFields = fields.filter(f => f.required && !mappedFields.includes(f.name));
  const missingRequired = unmappedRequiredFields.length > 0;
  
  const isConfirmDisabled = missingRequired || missingOneOf;
  const showRequirementsAlert = unmappedRequiredFields.length > 0 || unmappedOneOfGroups.length > 0;

  return (
    <Modal open={open} onClose={handleCancel}>
      <Box sx={{
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: '80%', maxHeight: '80vh', bgcolor: 'background.paper', boxShadow: 24, p: 4, overflow: 'auto'
      }}>
        <Typography variant="h6" gutterBottom>Importação em Lote</Typography>
        
        {showRequirementsAlert && (
          <Alert severity={isConfirmDisabled ? "warning" : "success"} sx={{ mb: 3 }}>
            <strong>Faltam Mapear (Obrigatórios):</strong> {
              [
                ...unmappedRequiredFields.map(f => f.label),
                ...unmappedOneOfGroups.map(group => {
                  const labels = group.map(n => fields.find(f => f.name === n)?.label || n);
                  return `(${labels.join(' E/OU ')})`;
                })
              ].join(', ')
            }
          </Alert>
        )}

        {!fileData.length ? (
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} p={4}>
            <Button variant="contained" component="label">
              Selecionar Arquivo (CSV, XLS, XLSX)
              <input type="file" hidden accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel" onChange={handleFileUpload} />
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle1" gutterBottom>Mapeamento de Colunas</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((col, idx) => (
                    <TableCell key={idx}>
                      <Typography variant="subtitle2">{col}</Typography>
                      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
                        <Select
                          value={mapping[col] || ""}
                          onChange={(e) => handleMapChange(col, e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value=""><em>-- Ignorar --</em></MenuItem>
                          {getAvailableFields(col).map(f => (
                            <MenuItem key={f.name} value={f.name}>{f.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {fileData.slice(0, 5).map((row, rIdx) => (
                  <TableRow key={rIdx}>
                    {columns.map((_, cIdx) => (
                      <TableCell key={cIdx}>{row[cIdx]}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
              <Button onClick={() => setFileData([])} variant="outlined">Voltar / Escolher outro arquivo</Button>
              <Button onClick={handleConfirm} variant="contained" disabled={isConfirmDisabled}>
                Confirmar Importação
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
